// src/pages/public/IntakeFormPage.js
// Route: /intake/:practitionerId/:token  (public — no auth required)
// Client fills out their intake form via a unique token link.

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { fetchIntakeFormData, submitIntakeResponse } from '../../services/intake-form-service';

const toTitleCase = (str) => {
  if (!str) return str;
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  fontSize: '15px',
  border: '1.5px solid #E5E7EB',
  borderRadius: '8px',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  color: '#111827',
  transition: 'border-color 0.15s ease',
};

const inputFocusStyle = { borderColor: '#7c3aed' };

const formatDeadline = (deadline) => {
  if (!deadline) return null;
  const d = deadline?.toDate ? deadline.toDate() : new Date(deadline._seconds ? deadline._seconds * 1000 : deadline);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};

// ─── QuestionField (interactive) ─────────────────────────────────────────────
const QuestionField = ({ q, value, onChange, error }) => {
  const [focused, setFocused] = useState(false);
  const style = { ...inputStyle, ...(focused ? inputFocusStyle : {}), ...(error ? { borderColor: '#EF4444' } : {}) };

  if (q.type === 'text') {
    return (
      <input
        type="text"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={q.placeholder || ''}
        style={style}
      />
    );
  }

  if (q.type === 'textarea') {
    return (
      <textarea
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        rows={4}
        placeholder={q.placeholder || ''}
        style={{ ...style, resize: 'vertical' }}
      />
    );
  }

  if (q.type === 'yes_no') {
    return (
      <div style={{ display: 'flex', gap: '20px' }}>
        {['Yes', 'No'].map(opt => (
          <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px', color: '#374151', cursor: 'pointer' }}>
            <input
              type="radio"
              name={`q_${q.id}`}
              value={opt}
              checked={value === opt}
              onChange={() => onChange(opt)}
              style={{ width: '18px', height: '18px', accentColor: '#7c3aed', cursor: 'pointer' }}
            />
            {opt}
          </label>
        ))}
      </div>
    );
  }

  if (q.type === 'select') {
    return (
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{ ...style, background: '#fff', cursor: 'pointer' }}
      >
        <option value="">{q.placeholder || 'Select an option…'}</option>
        {(q.options || []).map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
      </select>
    );
  }

  if (q.type === 'multiselect') {
    const selected = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      if (selected.includes(opt)) {
        onChange(selected.filter(s => s !== opt));
      } else {
        onChange([...selected, opt]);
      }
    };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {(q.options || []).map((opt, i) => (
          <label key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '15px', color: '#374151', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={selected.includes(opt)}
              onChange={() => toggle(opt)}
              style={{ width: '18px', height: '18px', accentColor: '#7c3aed', cursor: 'pointer' }}
            />
            {opt}
          </label>
        ))}
      </div>
    );
  }

  return null;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const IntakeFormPage = () => {
  const { practitionerId, token } = useParams();

  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [formData, setFormData] = useState(null);
  const [answers, setAnswers] = useState({}); // { [questionId]: string | string[] }
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!practitionerId || !token) {
      setFetchError('Invalid form link.');
      setLoading(false);
      return;
    }
    fetchIntakeFormData(practitionerId, token)
      .then(data => {
        setFormData(data);
        setLoading(false);
      })
      .catch(err => {
        setFetchError(err.message || 'Unable to load form.');
        setLoading(false);
      });
  }, [practitionerId, token]);

  const setAnswer = (qId, value) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
    if (errors[qId]) setErrors(prev => { const e = { ...prev }; delete e[qId]; return e; });
  };

  const validate = () => {
    if (!formData?.questions) return true;
    const newErrors = {};
    formData.questions.forEach(q => {
      if (!q.required) return;
      const val = answers[q.id];
      const isEmpty = !val || (Array.isArray(val) && val.length === 0) || val === '';
      if (isEmpty) newErrors[q.id] = 'This question is required.';
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitError(null);
    setSubmitting(true);

    try {
      // Build answers array with question labels denormalized
      const answersArray = (formData.questions || []).map(q => ({
        questionId: q.id,
        questionLabel: q.label,
        answer: answers[q.id] ?? '',
      }));

      await submitIntakeResponse(practitionerId, formData.responseId, token, answersArray);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ──
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid #E5E7EB', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ fontSize: '14px', color: '#6B7280' }}>Loading your form…</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Fetch error ──
  if (fetchError) {
    return (
      <div style={{ minHeight: '100vh', background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ background: '#fff', borderRadius: '16px', padding: '40px 32px', maxWidth: '440px', width: '100%', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: '1px solid #E5E7EB' }}>
          <div style={{ width: 56, height: 56, background: '#FEF2F2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <AlertCircle style={{ width: 28, height: 28, color: '#EF4444' }} />
          </div>
          <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>Form Not Found</h1>
          <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.6 }}>{fetchError}</p>
        </div>
      </div>
    );
  }

  // ── Already completed ──
  if (formData?.status === 'completed' && !submitted) {
    return (
      <div style={{ minHeight: '100vh', background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ background: '#fff', borderRadius: '16px', padding: '40px 32px', maxWidth: '440px', width: '100%', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: '1px solid #E5E7EB' }}>
          <div style={{ width: 56, height: 56, background: '#ECFDF5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <CheckCircle style={{ width: 28, height: 28, color: '#059669' }} />
          </div>
          <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>Already Submitted</h1>
          <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.6 }}>
            You've already submitted this form. Thank you!
          </p>
        </div>
      </div>
    );
  }

  // ── Expired ──
  const isExpired = formData?.status === 'expired' ||
    (formData?.deadline && new Date() > new Date(formData.deadline._seconds ? formData.deadline._seconds * 1000 : formData.deadline));

  if (isExpired && !submitted) {
    return (
      <div style={{ minHeight: '100vh', background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ background: '#fff', borderRadius: '16px', padding: '40px 32px', maxWidth: '440px', width: '100%', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: '1px solid #E5E7EB' }}>
          <div style={{ width: 56, height: 56, background: '#FFF7ED', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <Clock style={{ width: 28, height: 28, color: '#F59E0B' }} />
          </div>
          <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>Form Expired</h1>
          <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.6 }}>
            This form has expired. Please contact{' '}
            <strong style={{ color: '#111827' }}>{toTitleCase(formData?.practitionerName) || 'your practitioner'}</strong>{' '}
            directly.
          </p>
        </div>
      </div>
    );
  }

  // ── Success ──
  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
        <div style={{ background: '#fff', borderRadius: '16px', padding: '40px 32px', maxWidth: '440px', width: '100%', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: '1px solid #E5E7EB' }}>
          <div style={{ width: 64, height: 64, background: '#ECFDF5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <CheckCircle style={{ width: 32, height: 32, color: '#059669' }} />
          </div>
          <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#111827', marginBottom: '12px' }}>Thank You!</h1>
          <p style={{ fontSize: '15px', color: '#374151', lineHeight: 1.7 }}>
            Your responses have been sent to{' '}
            <strong style={{ color: '#111827' }}>{toTitleCase(formData?.practitionerName) || 'your practitioner'}</strong>.
            They'll review your answers before your session.
          </p>
        </div>
      </div>
    );
  }

  // ── Form ──
  const deadline = formData?.deadline;
  const deadlineStr = formatDeadline(deadline);

  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB', padding: '32px 16px' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'inline-block', padding: '6px 16px', background: '#F3F0FF', borderRadius: '999px', marginBottom: '16px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#7c3aed' }}>
              {toTitleCase(formData?.practitionerName)}
            </span>
          </div>
          <h1 style={{ fontSize: '26px', fontWeight: 700, color: '#111827', marginBottom: '8px', lineHeight: 1.3 }}>
            {formData?.formTitle}
          </h1>
          {formData?.clientName && (
            <p style={{ fontSize: '15px', color: '#6B7280' }}>Hi {formData.clientName.split(' ')[0]},</p>
          )}
        </div>

        {/* Card */}
        <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #E5E7EB', boxShadow: '0 4px 24px rgba(0,0,0,0.04)', overflow: 'hidden' }}>

          {/* Form description */}
          {formData?.formDescription && (
            <div style={{ padding: '24px 32px', borderBottom: '1px solid #F3F4F6', background: '#FAFAFA' }}>
              <p style={{ fontSize: '15px', color: '#374151', lineHeight: 1.7, margin: 0 }}>
                {formData.formDescription}
              </p>
            </div>
          )}

          {/* Deadline notice */}
          {deadlineStr && (
            <div style={{ padding: '12px 32px', borderBottom: '1px solid #F3F4F6', background: '#FFFBEB', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Clock style={{ width: 14, height: 14, color: '#D97706', flexShrink: 0 }} />
              <p style={{ fontSize: '13px', color: '#92400E', margin: 0 }}>
                Please complete this form by <strong>{deadlineStr}</strong>
              </p>
            </div>
          )}

          {/* Questions */}
          <form onSubmit={handleSubmit} style={{ padding: '32px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', marginBottom: '32px' }}>
              {(formData?.questions || []).map((q, i) => (
                <div key={q.id}>
                  <p style={{ fontSize: '15px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
                    {i + 1}. {q.label}
                    {q.required && <span style={{ color: '#EF4444', marginLeft: '4px' }}>*</span>}
                  </p>
                  <QuestionField
                    q={q}
                    value={answers[q.id]}
                    onChange={val => setAnswer(q.id, val)}
                    error={!!errors[q.id]}
                  />
                  {errors[q.id] && (
                    <p style={{ fontSize: '13px', color: '#EF4444', marginTop: '4px' }}>{errors[q.id]}</p>
                  )}
                </div>
              ))}
            </div>

            {/* Submit error */}
            {submitError && (
              <div style={{ padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', color: '#DC2626', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertCircle style={{ width: 16, height: 16, flexShrink: 0 }} />
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{
                width: '100%',
                padding: '14px',
                background: submitting ? '#A78BFA' : '#7c3aed',
                color: '#fff',
                border: 'none',
                borderRadius: '10px',
                fontSize: '16px',
                fontWeight: 700,
                cursor: submitting ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s ease',
                fontFamily: 'inherit',
              }}
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p style={{ textAlign: 'center', fontSize: '12px', color: '#9CA3AF', marginTop: '24px' }}>
          Powered by{' '}
          <span style={{ color: '#7c3aed', fontWeight: 600 }}>Divine Frameworks</span>
        </p>
      </div>
    </div>
  );
};

export default IntakeFormPage;
